import type { CharitySearchItem, CharitySuggestElements } from '../../types/pifster-contracts';

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function escapeHtml(str: string): string {
  return String(str)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

// ---------------------------------------------------------------------------
// Autocomplete dropdown
// ---------------------------------------------------------------------------

export function renderSearchResults(
  elements: CharitySuggestElements,
  items: readonly CharitySearchItem[],
): void {
  if (items.length === 0) {
    clearResults(elements);
    return;
  }

  let html = '';
  for (const item of items) {
    const label = item.charity_name;
    const status = item.charity_status;
    const postId = String(item.post_id);
    const url = item.charity_url;
    const normalized = item.normalized_name;

    if (!label || !postId) {
      continue;
    }

    html +=
      '<button type="button" class="pifster-charity-suggest__result"' +
      ` data-post-id="${escapeHtml(postId)}"` +
      ` data-url="${escapeHtml(url)}"` +
      ` data-name="${escapeHtml(label)}"` +
      ` data-status="${escapeHtml(status)}"` +
      ` data-normalized="${escapeHtml(normalized)}"` +
      '>' +
      `<span class="pifster-charity-suggest__result-name">${escapeHtml(label)}</span>` +
      (status
        ? `<span class="pifster-charity-suggest__result-status">${escapeHtml(status)}</span>`
        : '') +
      '</button>';
  }

  elements.resultsEl.innerHTML = html;
  elements.resultsEl.hidden = false;
}

export function clearResults(elements: CharitySuggestElements): void {
  elements.resultsEl.innerHTML = '';
  elements.resultsEl.hidden = true;
}

// ---------------------------------------------------------------------------
// Status / hint messaging
// ---------------------------------------------------------------------------

export type StatusType = '' | 'error' | 'success' | 'busy';

export function setStatus(
  elements: CharitySuggestElements,
  message: string,
  type: StatusType,
): void {
  elements.statusEl.textContent = message;
  elements.statusEl.setAttribute('data-status', type);
}

export function setHint(elements: CharitySuggestElements, message: string): void {
  if (!elements.hintEl) {
    return;
  }
  elements.hintEl.textContent = message;
  elements.hintEl.hidden = message === '';
}

// ---------------------------------------------------------------------------
// Loading spinner (immediate class toggle — timer logic lives in orchestrator)
// ---------------------------------------------------------------------------

export function showLoadingSpinner(elements: CharitySuggestElements): void {
  elements.autocompleteWrap?.classList.add('is-loading');
}

export function hideLoadingSpinner(elements: CharitySuggestElements): void {
  elements.autocompleteWrap?.classList.remove('is-loading');
}

// ---------------------------------------------------------------------------
// Clear button visibility
// ---------------------------------------------------------------------------

export function setClearVisible(elements: CharitySuggestElements, visible: boolean): void {
  if (!elements.clearBtn) {
    return;
  }
  elements.clearBtn.hidden = !visible;
  elements.autocompleteWrap?.classList.toggle('has-clear', visible);
}

// ---------------------------------------------------------------------------
// Submit button state
// ---------------------------------------------------------------------------

export function setSubmitEnabled(elements: CharitySuggestElements, enabled: boolean): void {
  elements.submitBtn.disabled = !enabled;
}
